import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs/esm';

import { isPresent } from 'app/core/util/operators';
import { ApplicationConfigService } from 'app/core/config/application-config.service';
import { createRequestOption } from 'app/core/request/request-util';
import { ICbicDocMst, getCbicDocMstIdentifier } from '../cbic-doc-mst.model';

export type EntityResponseType = HttpResponse<ICbicDocMst>;
export type EntityArrayResponseType = HttpResponse<ICbicDocMst[]>;

@Injectable({ providedIn: 'root' })
export class CbicDocMstService {
  protected resourceUrl = this.applicationConfigService.getEndpointFor('api/cbic-doc-msts');
  protected filePathUrl = this.applicationConfigService.getEndpointFor('api/getFilepath');
  protected fileAllPathUrl = this.applicationConfigService.getEndpointFor('api/Filepath');
  protected PathUrl = this.applicationConfigService.getEndpointFor('api/Filepaths');
  constructor(protected http: HttpClient, protected applicationConfigService: ApplicationConfigService) {}

  create(cbicDocMst: ICbicDocMst): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(cbicDocMst);
    return this.http
      .post<ICbicDocMst>(this.resourceUrl, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(cbicDocMst: ICbicDocMst): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(cbicDocMst);
    return this.http
      .put<ICbicDocMst>(`${this.resourceUrl}/${getCbicDocMstIdentifier(cbicDocMst) as number}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(cbicDocMst: ICbicDocMst): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(cbicDocMst);
    return this.http
      .patch<ICbicDocMst>(`${this.resourceUrl}/${getCbicDocMstIdentifier(cbicDocMst) as number}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http
      .get<ICbicDocMst>(`${this.resourceUrl}/${id}`, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<ICbicDocMst[]>(this.resourceUrl, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }


  getFilePath(contTypeID:number):Observable<EntityArrayResponseType>{
    return this.http.get<ICbicDocMst[]>(`${this.filePathUrl}/${contTypeID}`, {  observe: 'response' })
     .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));

  }    

  getFileallDoc():Observable<EntityArrayResponseType>{
    return this.http.get<ICbicDocMst[]>(`${this.fileAllPathUrl}`, {  observe: 'response' })
     .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));

  } 

  fetchMemberCustomsPdfPath(contTypeID:number):Observable<EntityResponseType>{
    return this.http.get<ICbicDocMst>(`${this.filePathUrl}/${contTypeID}`, {  observe: 'response' })
   .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));

  }

  getDocList(whatsNewDataList:string,contentTypeId:number):Observable<EntityArrayResponseType>{
    return this.http.get<ICbicDocMst[]>(`${this.fileAllPathUrl}/${contentTypeId}/${whatsNewDataList}`, {  observe: 'response' })
     .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));

  } 

  gettickerDocList(tickersNewList:string,contentTypeId:number):Observable<EntityArrayResponseType>{
    return this.http.get<ICbicDocMst[]>(`${this.fileAllPathUrl}/${contentTypeId}/${tickersNewList}`, {  observe: 'response' })
     .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));

  } 
  
  getbuggageRulesNew(tickersNewList:string,contentTypeId:number):Observable<EntityArrayResponseType>{
    return this.http.get<ICbicDocMst[]>(`${this.fileAllPathUrl}/${contentTypeId}/${tickersNewList}`, {  observe: 'response' })
     .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));

  }
  getfatchOther(tickersNewList:string,contentTypeId:number):Observable<EntityArrayResponseType>{
    return this.http.get<ICbicDocMst[]>(`${this.fileAllPathUrl}/${contentTypeId}/${tickersNewList}`, {  observe: 'response' })
     .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));

  }

  addCbicDocMstToCollectionIfMissing(
    cbicDocMstCollection: ICbicDocMst[],
    ...cbicDocMstsToCheck: (ICbicDocMst | null | undefined)[]
  ): ICbicDocMst[] {
    const cbicDocMsts: ICbicDocMst[] = cbicDocMstsToCheck.filter(isPresent);
    if (cbicDocMsts.length > 0) {
      const cbicDocMstCollectionIdentifiers = cbicDocMstCollection.map(cbicDocMstItem => getCbicDocMstIdentifier(cbicDocMstItem)!);
      const cbicDocMstsToAdd = cbicDocMsts.filter(cbicDocMstItem => {
        const cbicDocMstIdentifier = getCbicDocMstIdentifier(cbicDocMstItem);
        if (cbicDocMstIdentifier == null || cbicDocMstCollectionIdentifiers.includes(cbicDocMstIdentifier)) {
          return false;
        }
        cbicDocMstCollectionIdentifiers.push(cbicDocMstIdentifier);
        return true;
      });
      return [...cbicDocMstsToAdd, ...cbicDocMstCollection];
    }
    return cbicDocMstCollection;
  }

  protected convertDateFromClient(cbicDocMst: ICbicDocMst): ICbicDocMst {
    return Object.assign({}, cbicDocMst, {
      createdDt: cbicDocMst.createdDt?.isValid() ? cbicDocMst.createdDt.toJSON() : undefined,
      updatedDt: cbicDocMst.updatedDt?.isValid() ? cbicDocMst.updatedDt.toJSON() : undefined,
      publishDt: cbicDocMst.publishDt?.isValid() ? cbicDocMst.publishDt.toJSON() : undefined,
      archiveDt: cbicDocMst.archiveDt?.isValid() ? cbicDocMst.archiveDt.toJSON() : undefined,
      documentDt: cbicDocMst.documentDt?.isValid() ? cbicDocMst.documentDt.toJSON() : undefined,
      refDt1: cbicDocMst.refDt1?.isValid() ? cbicDocMst.refDt1.toJSON() : undefined,
      refDt2: cbicDocMst.refDt2?.isValid() ? cbicDocMst.refDt2.toJSON() : undefined,
      expiryDt: cbicDocMst.expiryDt?.isValid() ? cbicDocMst.expiryDt.toJSON() : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.createdDt = res.body.createdDt ? dayjs(res.body.createdDt) : undefined;
      res.body.updatedDt = res.body.updatedDt ? dayjs(res.body.updatedDt) : undefined;
      res.body.publishDt = res.body.publishDt ? dayjs(res.body.publishDt) : undefined;
      res.body.archiveDt = res.body.archiveDt ? dayjs(res.body.archiveDt) : undefined;
      res.body.documentDt = res.body.documentDt ? dayjs(res.body.documentDt) : undefined;
      res.body.refDt1 = res.body.refDt1 ? dayjs(res.body.refDt1) : undefined;
      res.body.refDt2 = res.body.refDt2 ? dayjs(res.body.refDt2) : undefined;
      res.body.expiryDt = res.body.expiryDt ? dayjs(res.body.expiryDt) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((cbicDocMst: ICbicDocMst) => {
        cbicDocMst.createdDt = cbicDocMst.createdDt ? dayjs(cbicDocMst.createdDt) : undefined;
        cbicDocMst.updatedDt = cbicDocMst.updatedDt ? dayjs(cbicDocMst.updatedDt) : undefined;
        cbicDocMst.publishDt = cbicDocMst.publishDt ? dayjs(cbicDocMst.publishDt) : undefined;
        cbicDocMst.archiveDt = cbicDocMst.archiveDt ? dayjs(cbicDocMst.archiveDt) : undefined;
        cbicDocMst.documentDt = cbicDocMst.documentDt ? dayjs(cbicDocMst.documentDt) : undefined;
        cbicDocMst.refDt1 = cbicDocMst.refDt1 ? dayjs(cbicDocMst.refDt1) : undefined;
        cbicDocMst.refDt2 = cbicDocMst.refDt2 ? dayjs(cbicDocMst.refDt2) : undefined;
        cbicDocMst.expiryDt = cbicDocMst.expiryDt ? dayjs(cbicDocMst.expiryDt) : undefined;
      });
    }
    return res;
  }
}
